import React, { useState, useEffect } from 'react';
import { useI18n } from '../hooks/useI18n';
import { useAuth } from '../hooks/useAuth';
import { useDocuments } from '../hooks/useDocuments';
import { DocumentTabs, DocFilterTab } from '../components/documents/DocumentTabs';
import { DocumentItem } from '../components/documents/DocumentItem';
import { UploadModal } from '../components/documents/UploadModal';
import { PrescriptionModal } from '../components/documents/PrescriptionModal';
import { ReferralModal } from '../components/documents/ReferralModal';
import { ConsultationCard } from '../components/consultations/ConsultationCard';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { FullscreenLoader } from '../components/ui/FullscreenLoader';
import { toast } from '../components/ui/Toast';
import { DocumentItem as IDocumentItem } from '../types/cabinet.types';
import { useConsultations } from '../hooks/useConsultations';
import { SummaryReferral } from '../types/visit-summary.types';
import { getPatientReferrals } from '../api/cabinetApi';

export const DocumentsPage: React.FC = () => {
  const { t } = useI18n();
  const { user, verifyEmail } = useAuth();
  const { documents, isLoading, isDeleting, uploadBlocked, uploadFile, deleteFile } = useDocuments();
  const { consultations, isLoading: isConsultationsLoading } = useConsultations();

  const [activeTab, setActiveTab] = useState<DocFilterTab>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [selectedPrescription, setSelectedPrescription] = useState<IDocumentItem | null>(null);
  const [selectedReferral, setSelectedReferral] = useState<SummaryReferral | null>(null);

  // Скерування з медик-візитів — завантажуємо у фоні лише коли активна відповідна вкладка
  // або після рендеру (низький пріоритет, не блокує відображення)
  const [visitReferrals, setVisitReferrals] = useState<Array<SummaryReferral>>([]);
  const [referralsLoaded, setReferralsLoaded] = useState(false);

  // Завантажуємо referrals у фоні після первинного рендеру (defer)
  useEffect(() => {
    if (referralsLoaded) return;
    
    getPatientReferrals()
      .then(results => {
        // Фільтруємо ті, що є електронними скеруваннями з кодом
        const all = results.filter(r => r.SendAsESkierowanie && r.ESkierowanieCode);
        setVisitReferrals(all);
        setReferralsLoaded(true);
      })
      .catch(() => {
        setReferralsLoaded(true); // навіть при помилці вважаємо завантаженими, щоб не спамити запитами
      });
  }, [referralsLoaded]);

  // Email verification gate check
  if (user && !user.emailVerified) {
    const handleVerify = async () => {
      setIsVerifying(true);
      try {
        await verifyEmail();
        toast(t('verify_email_sent'));
      } catch (e) {
        toast('Failed to send email. Please try again.');
      } finally {
        setIsVerifying(false);
      }
    };

    return (
      <div style={{ paddingTop: '20px' }}>
        <h1 className="cab-page-title">{t('nav_doc')}</h1>
        <div className="cab-card" style={{ textAlign: 'center', padding: '40px 20px', marginTop: '20px' }}>
          <div style={{ color: 'var(--cab-v500)', fontSize: '40px', marginBottom: '16px' }}>
            ✉️
          </div>
          <p style={{ fontSize: '15px', fontWeight: 600, color: '#111', marginBottom: '8px' }}>
            {t('verify_email_banner')}
          </p>
          <Button
            variant="primary"
            onClick={handleVerify}
            isLoading={isVerifying}
            style={{ marginTop: '20px' }}
          >
            {t('verify_email_cta')}
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || isConsultationsLoading) {
    return <FullscreenLoader inline />;
  }

  const skierowaniaConsultations = consultations.filter(c => !!c.skierowanieUrl);

  const validDocs = documents.filter(doc => {
    const isPrescription = doc.type === 'recepta' || !!doc.drugs;
    if (isPrescription && !doc.ereceptaCode) {
      return false;
    }
    return true;
  });

  const filteredDocs = activeTab === 'all'
    ? validDocs
    : validDocs.filter(doc => {
        if (activeTab === 'wyniki') {
          return doc.type === 'wyniki' || (doc.title && doc.title.toLowerCase().includes('synevo'));
        }
        return doc.type === activeTab;
      });

  const handleDeleteRequest = (doc: IDocumentItem) => {
    setDeleteConfirmId(doc.id);
  };

  const handleDeleteConfirm = async () => {
    const doc = documents.find(d => d.id === deleteConfirmId);
    if (!doc) return;
    setDeleteConfirmId(null);
    const ok = await deleteFile(doc);
    if (ok) {
      toast(t('toast_deleted'));
    } else {
      toast(t('doc_delete_err'));
    }
  };

  return (
    <div>
      {/* Заголовок + кнопка Dodaj plik */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="cab-page-title" style={{ margin: 0 }}>
          <span className="cab-title-desktop">{t('doc_title_desktop')}</span>
          <span className="cab-title-mobile">{t('doc_title_mobile')}</span>
        </h1>
        <button
          className="cab-add-doc-btn"
          onClick={() => setIsModalOpen(true)}
          disabled={uploadBlocked}
          title={uploadBlocked ? t('upload_blocked_hint') : t('add_file')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span className="cab-add-btn-txt">{t('add_file')}</span>
        </button>
      </div>

      <div className="cab-doc-divider"></div>

      {/* Banner: upload недоступний до першої консультації */}
      {uploadBlocked && (
        <div className="cab-upload-blocked-banner">
          <span className="cab-ubb-ic">🔒</span>
          <span>{t('upload_blocked_hint')}</span>
        </div>
      )}

      <DocumentTabs activeTab={activeTab} onChangeTab={setActiveTab} />

      {filteredDocs.length === 0 && (activeTab !== 'skierowanie' || skierowaniaConsultations.length === 0) && (activeTab !== 'all' || skierowaniaConsultations.length === 0) ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          padding: '80px 20px',
          color: '#B5A6D4'
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="9.5" y1="13.5" x2="14.5" y2="18.5"></line>
            <line x1="14.5" y1="13.5" x2="9.5" y2="18.5"></line>
          </svg>
          <span style={{ fontSize: '22px', fontWeight: 600 }}>{t('doc_no_files')}</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {(activeTab === 'skierowanie' || activeTab === 'all') && skierowaniaConsultations.map(cons => (
            <DocumentItem 
              key={`cons-${cons.id}`} 
              document={{
                id: cons.id,
                wpId: null,
                title: cons.serviceName ? `Skierowanie: ${cons.serviceName} (Synevo)` : 'Skierowanie Synevo',
                date: cons.date,
                type: 'skierowanie',
                url: cons.skierowanieUrl || '',
                source: 'medyc',
                mimeType: 'application/pdf'
              }} 
            />
          ))}
          {/* e-Skierowania з медик-візитів */}
          {(activeTab === 'skierowanie' || activeTab === 'all') && visitReferrals.map((ref, idx) => (
            <DocumentItem
              key={`ref-${ref.Id}-${idx}`}
              document={{
                id: `ref_${ref.Id}`,
                wpId: null,
                title: ref.TypeName ? `${t('doc_referral_card_label')}: ${ref.TypeName}` : t('doc_referral_card_label'),
                date: ref.CreatedAt || '',
                type: 'skierowanie',
                url: '',
                source: 'medyc',
                ereceptaCode: ref.ESkierowanieCode || undefined,
              }}
              onClick={() => setSelectedReferral(ref)}
            />
          ))}
          {filteredDocs.map(doc => (
            <DocumentItem
              key={doc.id}
              document={doc}
              onDelete={doc.canDelete ? handleDeleteRequest : undefined}
              onClick={doc.drugs ? () => setSelectedPrescription(doc) : undefined}
              isDeleting={isDeleting === doc.id}
            />
          ))}
        </div>
      )}

      {/* Modal upload */}
      <UploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpload={async (file) => {
          await uploadFile(file);
          toast(t('toast_added'));
        }}
      />

      <PrescriptionModal
        isOpen={!!selectedPrescription}
        onClose={() => setSelectedPrescription(null)}
        document={selectedPrescription}
      />

      <ReferralModal
        isOpen={!!selectedReferral}
        onClose={() => setSelectedReferral(null)}
        referral={selectedReferral}
      />

      {/* Confirm delete dialog */}
      {deleteConfirmId && (
        <div className="cab-confirm-overlay" onClick={() => setDeleteConfirmId(null)}>
          <div className="cab-confirm-box" onClick={e => e.stopPropagation()}>
            <p className="cab-confirm-msg">{t('doc_delete_confirm')}</p>
            <div className="cab-confirm-btns">
              <button className="cab-confirm-cancel" onClick={() => setDeleteConfirmId(null)}>
                {t('cancel')}
              </button>
              <button className="cab-confirm-ok" onClick={handleDeleteConfirm}>
                {t('doc_delete_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .cab-title-mobile {
          display: none;
        }
        .cab-doc-divider {
          height: 1px;
          background: #F0EDF6;
          margin: 20px 0;
        }
        .cab-add-doc-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          height: 40px;
          padding: 0 20px;
          border-radius: 100px;
          background: var(--cab-violet);
          color: #fff;
          border: none;
          cursor: pointer;
          transition: opacity 0.2s, background 0.2s;
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 600;
          white-space: nowrap;
        }
        .cab-add-doc-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .cab-add-doc-btn:not(:disabled):hover {
          background: #2a115e;
        }
        .cab-add-doc-label {
          display: none;
        }
        
        @media (min-width: 400px) {
          .cab-add-doc-label {
            display: inline;
          }
        }
        
        @media (max-width: 1024px) {
          .cab-title-desktop {
            display: none;
          }
          .cab-title-mobile {
            display: inline;
          }
          .cab-doc-divider {
            display: none; /* In mobile Figma screenshot there is no divider */
          }
          .cab-add-doc-btn {
            
            color: #fff;
          }
          .cab-add-doc-btn:not(:disabled):hover {
            background: #D6C1F1;
          }
        }

        .cab-upload-blocked-banner {
          display: flex;
          align-items: center;
          gap: 10px;
          background: #FFF8E6;
          border: 1px solid #F6C94E;
          border-radius: 12px;
          padding: 12px 16px;
          margin-top: 16px;
          font-size: 13.5px;
          color: #7A5A00;
          font-family: 'Poppins', sans-serif;
        }
        .cab-ubb-ic {
          font-size: 18px;
          flex-shrink: 0;
        }
        .cab-confirm-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }
        .cab-confirm-box {
          background: #fff;
          border-radius: 20px;
          padding: 28px 24px;
          max-width: 340px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        }
        .cab-confirm-msg {
          font-family: 'Poppins', sans-serif;
          font-size: 15px;
          font-weight: 600;
          color: #111;
          margin: 0 0 24px;
          line-height: 1.5;
          text-align: center;
        }
        .cab-confirm-btns {
          display: flex;
          gap: 12px;
        }
        .cab-confirm-cancel,
        .cab-confirm-ok {
          flex: 1;
          height: 44px;
          border-radius: 100px;
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: 0.2s;
        }
        .cab-confirm-cancel {
          background: #F4F0FA;
          color: var(--cab-violet);
        }
        .cab-confirm-cancel:hover { background: #E6D6FA; }
        .cab-confirm-ok {
          background: #E53E3E;
          color: #fff;
        }
        .cab-confirm-ok:hover { background: #C53030; }
      `}</style>
    </div>
  );
};
